import React from "react";
import { storageAvailable } from "../lib/safeStorage";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6 font-body">
    <div className="max-w-md w-full text-center" data-testid="app-error-fallback">{children}</div>
  </div>
);

const hardReset = () => {
  try { window.localStorage.clear(); } catch { /* ignore */ }
  try { window.sessionStorage.clear(); } catch { /* ignore */ }
  try { indexedDB.deleteDatabase("ss_keys"); } catch { /* ignore */ }
  setTimeout(() => window.location.reload(), 120);
};

// Catches any render/runtime crash so members never hit a raw white screen.
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch() { /* no-op: fallback UI is enough */ }

  render() {
    if (!this.state.error) return this.props.children;

    const blocked = !storageAvailable();
    return (
      <Shell>
        <div className="font-heading text-2xl text-white mb-3">Signal interrupted</div>
        {blocked ? (
          <p className="text-sm text-zinc-400 leading-relaxed mb-6">
            Your browser is blocking the secure on-device storage this network needs.
            If you're on <span className="text-white">Brave</span>, tap the{" "}
            <span className="text-white">Shields</span> (lion) icon and set Shields{" "}
            <span className="text-white">Down</span> for this site. If you're in a{" "}
            <span className="text-white">Private/Incognito</span> tab, open it in a normal tab. Then reload.
          </p>
        ) : (
          <p className="text-sm text-zinc-400 leading-relaxed mb-6">
            Something didn't load correctly. This is usually a stale cache, a reload almost always fixes it.
          </p>
        )}
        <div className="flex flex-col gap-3">
          <button
            onClick={() => window.location.reload()}
            data-testid="error-reload-button"
            className="w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-5 py-3 hover:bg-[#2BC4B5] transition-colors"
          >
            Reload
          </button>
          <button
            onClick={hardReset}
            data-testid="error-reset-button"
            className="w-full border border-white/15 text-zinc-300 px-5 py-3 text-sm hover:bg-white/5 transition-colors"
          >
            Clear local data &amp; restart
          </button>
        </div>
        <p className="text-xs text-zinc-600 mt-6">
          Clearing local data signs you out on this device. You'll sign back in with your password or 12-word recovery phrase.
        </p>
      </Shell>
    );
  }
}
